import apiClient from '../utils/apiClient';

/**
 * Debit Note Service
 * Handles all API calls related to debit notes
 */
const debitNoteService = {
  /**
   * Get all debit notes with optional filtering
   * @param {Object} filters - Filter parameters
   * @param {Number} page - Page number for pagination
   * @param {Number} limit - Number of items per page
   * @returns {Promise} - Promise resolving to debit notes data
   */
  getDebitNotes: async (filters = {}, page = 1, limit = 10) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      // Add pagination
      queryParams.append('page', page);
      queryParams.append('limit', limit);
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const response = await apiClient.get(`/debitNotes?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching debit notes:', error);
      throw error.response?.data || { message: 'Error fetching debit notes' };
    }
  },
  
  /**
   * Get a single debit note by ID
   * @param {String} id - Debit note ID
   * @returns {Promise} - Promise resolving to debit note data
   */
  getDebitNoteById: async (id) => {
    try {
      const response = await apiClient.get(`/debitNotes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching debit note ${id}:`, error);
      throw error.response?.data || { message: `Error fetching debit note ${id}` };
    }
  },
  
  /**
   * Create a new debit note
   * @param {Object} debitNoteData - Debit note data
   * @returns {Promise} - Promise resolving to created debit note
   */
  createDebitNote: async (debitNoteData) => {
    try {
      const response = await apiClient.post('/debitNotes', debitNoteData);
      return response.data;
    } catch (error) {
      console.error('Error creating debit note:', error);
      throw error.response?.data || { message: 'Error creating debit note' };
    }
  },
  
  /**
   * Update an existing debit note
   * @param {String} id - Debit note ID
   * @param {Object} debitNoteData - Updated debit note data
   * @returns {Promise} - Promise resolving to updated debit note
   */
  updateDebitNote: async (id, debitNoteData) => {
    try {
      const response = await apiClient.put(`/debitNotes/${id}`, debitNoteData);
      return response.data;
    } catch (error) {
      console.error(`Error updating debit note ${id}:`, error);
      throw error.response?.data || { message: `Error updating debit note ${id}` };
    }
  },
  
  /**
   * Delete a debit note
   * @param {String} id - Debit note ID
   * @returns {Promise} - Promise resolving to deletion result
   */
  deleteDebitNote: async (id) => {
    try {
      const response = await apiClient.delete(`/debitNotes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting debit note ${id}:`, error);
      throw error.response?.data || { message: `Error deleting debit note ${id}` };
    }
  },
  
  /**
   * Get the next debit note number
   * @returns {Promise} - Promise resolving to next debit note number
   */
  getNextDebitNoteNumber: async () => {
    try {
      const response = await apiClient.get('/debitNotes/next-invoice-number');
      return response.data;
    } catch (error) {
      console.error('Error fetching next debit note number:', error);
      throw error.response?.data || { message: 'Error fetching next debit note number' };
    }
  }
};

export default debitNoteService;
